import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useOnlineStatus } from 'hooks/useOnlineStatus'
import { RefreshCw, CheckCircle, AlertTriangle, X } from 'lucide-react'
import { useOfflineBootstrapStatus } from 'hooks/useOfflineBootstrapStatus'

const SESSION_AUTH_BOOTSTRAP_BANNER_KEY = 'erp_offline_bootstrap_success_banner_auth'

/**
 * Top app banners for offline bootstrap / sync only.
 * Offline connectivity itself is shown via the header ConnectionStatusChip.
 */
const OfflineBanner: React.FC = () => {
 const { t } = useTranslation()
 const { isOnline } = useOnlineStatus()
 const { status: bootstrapStatus, bootstrapRunning, syncing, syncNow, summary } = useOfflineBootstrapStatus()
 const [showBootstrapSuccess, setShowBootstrapSuccess] = useState(false)
 const previousBootstrapStatusRef = useRef<string | null>(null)

 useEffect(() => {
 const prev = previousBootstrapStatusRef.current
 const next = bootstrapStatus?.status ?? null
 const reason = bootstrapStatus?.reason ?? ''
 previousBootstrapStatusRef.current = next
 if (prev !== 'running' || next !== 'success') {
 return
 }
 if (reason === 'manual-sync') {
 return
 }
 if (reason === 'auth-restore') {
 try {
 if (sessionStorage.getItem(SESSION_AUTH_BOOTSTRAP_BANNER_KEY) === '1') {
 return
 }
 sessionStorage.setItem(SESSION_AUTH_BOOTSTRAP_BANNER_KEY, '1')
 } catch {
 /* ignore */
 }
 }
 setShowBootstrapSuccess(true)
 }, [bootstrapStatus?.status, bootstrapStatus?.reason])

 useEffect(() => {
 if (!showBootstrapSuccess) return
 const timer = window.setTimeout(() => setShowBootstrapSuccess(false), 3500)
 return () => window.clearTimeout(timer)
 }, [showBootstrapSuccess])

 const hasBootstrapFailure =
 isOnline && (bootstrapStatus?.status === 'partial_failed' || bootstrapStatus?.status === 'failed')

 const isVisible =
 (bootstrapRunning && isOnline) || hasBootstrapFailure || showBootstrapSuccess

 useEffect(() => {
 document.body.classList.toggle('has-app-banner', isVisible)
 return () => {
 document.body.classList.remove('has-app-banner')
 }
 }, [isVisible])

 if (!isVisible) return null

 if (showBootstrapSuccess) {
 return (
 <div className="fixed top-0 left-0 right-0 z-[9999] h-10 bg-success text-success-foreground px-4 flex items-center justify-center gap-2 text-sm shadow-lg transition-all">
 <CheckCircle className="h-4 w-4 shrink-0"/>
 <span>{t('offline.readyForOffline')}</span>
 <button
 type="button" onClick={() => setShowBootstrapSuccess(false)}
 className="absolute end-3 top-1/2 -translate-y-1/2 rounded p-1 text-success-foreground/90 hover:bg-success-foreground/15 hover:text-success-foreground" aria-label={t('offline.close')}>
 <X className="h-4 w-4"/>
 </button>
 </div>
 )
 }

 if (bootstrapRunning && isOnline) {
 return (
 <div className="fixed top-0 left-0 right-0 z-[9999] h-10 bg-info text-info-foreground px-4 flex items-center justify-center gap-2 text-sm shadow-lg">
 <RefreshCw className="h-4 w-4 animate-spin"/>
 <span>{t('offline.preparingForOffline')}</span>
 </div>
 )
 }

 if (hasBootstrapFailure) {
 const isFailed = bootstrapStatus?.status === 'failed'
 const failureHint = summary.failedModuleDetails
 .slice(0, 2)
 .map((detail) => `${detail.label}${detail.error ? ` · ${detail.error}` : ''}`)
 .join(' · ')
 return (
 <div className={`fixed top-0 left-0 right-0 z-[9999] min-h-10 ${isFailed ? 'bg-destructive text-destructive-foreground' : 'bg-warning text-warning-foreground'} px-4 py-2 flex flex-wrap items-center justify-center gap-2 text-sm shadow-lg`}>
 <AlertTriangle className="h-4 w-4 shrink-0"/>
 <span>{bootstrapStatus?.message || t('offline.partialBootstrap')}</span>
 {summary.failedModules > 0 && (
 <span className=" rounded-full border border-current/20 bg-background/15 px-2 py-0.5 text-xs font-semibold">{t('offline.failedCount', { count: summary.failedModules })}</span>
 )}
 {failureHint && (
 <span className="max-w-[min(100%,48rem)] truncate text-xs opacity-90">{failureHint}</span>
 )}
 <button
 type="button" onClick={() => { void syncNow() }}
 disabled={syncing}
 className=" rounded-full border border-current/20 bg-background/15 px-3 py-0.5 text-xs font-semibold hover:bg-background/25 disabled:">
 {syncing ? t('offline.syncing') : t('offline.retry')}
 </button>
 </div>
 )
 }

 return null
}

export default OfflineBanner
